import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Empty, Row, Segmented, Space, Tag, Typography, message } from "antd";
import "remixicon/fonts/remixicon.css";
import { useGlobal } from "../contexts/GlobalContext";
import request from "../utils/request";
import { useTranslation } from "../i18n";

const { Text, Paragraph } = Typography;

interface PublicModel {
  id: number;
  modelName: string;
  displayName?: string;
  modelType?: string;
  maxTokens?: number;
  contextWindow?: number;
  isDefault?: boolean;
  supportsToolCalls?: boolean;
  supportsImage?: boolean;
  inputPricePer1k?: number;
  outputPricePer1k?: number;
  pendingPrice?: boolean;
}

interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

const tokensToK = (value?: number) => (value == null ? value : Number((value / 1000).toFixed(3)));

const formatPrice = (value?: number) => {
  if (value == null || value === 0) return "—";
  return `¥${value.toFixed(4)}/1k`;
};

type CapabilityFilter = "all" | "tools" | "image";

const ModelCenter: React.FC = () => {
  const { t } = useTranslation();
  const { API_BASE_URL, token } = useGlobal();
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState<PublicModel[]>([]);
  const [capability, setCapability] = useState<CapabilityFilter>("all");

  useEffect(() => {
    if (!token) return;

    setLoading(true);
    request
      .get<ApiResponse<PublicModel[]>>(`${API_BASE_URL}/api/v3/models`)
      .then((response) => {
        if (response.data.code === 200) {
          setModels(response.data.data || []);
          return;
        }
        message.error(response.data.msg || t("modelCenter.fetchFailed"));
      })
      .catch((error) => {
        console.error("获取模型列表失败:", error);
        message.error(t("modelCenter.fetchFailedRetry"));
      })
      .finally(() => setLoading(false));
  }, [API_BASE_URL, token, t]);

  // 按能力过滤(与通用 ModelSelect 的 requireTools/requireVision 同口径)
  const filtered = useMemo(() => {
    return models.filter((m) => {
      if (capability === "tools") return !!m.supportsToolCalls;
      if (capability === "image") return !!m.supportsImage;
      return true;
    });
  }, [models, capability]);

  return (
    <div style={{ background: "#f0f2f5", minHeight: "100%", borderRadius: 8, padding: 16 }}>
      <Card
        title={
          <Space>
            <i className="ri-cpu-line" style={{ color: "#1677ff", fontSize: 20 }} />
            <span>{t("nav.models")}</span>
          </Space>
        }
        extra={
          <Segmented
            value={capability}
            onChange={(value) => setCapability(value as CapabilityFilter)}
            options={[
              { label: t("modelCenter.filterAll"), value: "all" },
              { label: t("modelCenter.filterTools"), value: "tools" },
              { label: t("modelCenter.filterImage"), value: "image" },
            ]}
          />
        }
        variant="outlined"
        loading={loading}
      >
        {filtered.length === 0 ? (
          <Empty description={t("modelCenter.empty")} />
        ) : (
          <Row gutter={[16, 16]}>
            {filtered.map((model) => (
              <Col xs={24} sm={12} xl={8} key={model.id}>
                <Card size="small" hoverable style={{ height: "100%" }}>
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <Space wrap align="center">
                      <Text strong style={{ fontSize: 15 }}>
                        {model.displayName || model.modelName || t("modelCenter.modelN", { id: model.id })}
                      </Text>
                      {model.isDefault && <Tag color="processing">{t("modelCenter.default")}</Tag>}
                      {model.modelType && <Tag>{t(`modelCenter.type.${model.modelType}`, { defaultValue: model.modelType })}</Tag>}
                    </Space>

                    <Space wrap size={4}>
                      {model.supportsToolCalls && <Tag color="geekblue">Tools</Tag>}
                      {model.supportsImage && <Tag color="purple">Image</Tag>}
                      {!model.supportsToolCalls && !model.supportsImage && (
                        <Text type="secondary">{t("modelCenter.basicChat")}</Text>
                      )}
                    </Space>

                    <Space split={<span style={{ color: "#d9d9d9" }}>|</span>} wrap>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("modelCenter.context")} {model.contextWindow ? `${tokensToK(model.contextWindow)?.toLocaleString()}k` : "—"}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("modelCenter.maxOutput")} {model.maxTokens ? `${tokensToK(model.maxTokens)?.toLocaleString()}k` : "—"}
                      </Text>
                    </Space>

                    {model.pendingPrice ? (
                      <Tag color="default">{t("modelCenter.pendingPrice")}</Tag>
                    ) : (
                      <Paragraph style={{ margin: 0 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t("modelCenter.input")} </Text>
                        <Text>{formatPrice(model.inputPricePer1k)}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}> ·  {t("modelCenter.output")} </Text>
                        <Text>{formatPrice(model.outputPricePer1k)}</Text>
                      </Paragraph>
                    )}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>
    </div>
  );
};

export default ModelCenter;
